"use client"

import { useEffect, useState } from "react"
import { formatTime } from "../helpers"

export const Stage = {
    Loading: 0,
    Waiting: 1,
    Preparing: 2,
    Processing: 3,
    Done: 4,
} as const

interface Props {
    stage: 0 | 1 | 2 | 3 | 4
}

const steps = [
    { name: "Preparing", stage: Stage.Preparing },
    { name: "Processing", stage: Stage.Processing },
    { name: "Done", stage: Stage.Done },
]

export function Progress({ stage }: Props) {
    const [startTime, setStartTime] = useState<number>(Date.now())
    const [timers, setTimers] = useState<{
        [Stage.Preparing]: number
        [Stage.Processing]: number
    }>({
        [Stage.Preparing]: 0,
        [Stage.Processing]: 0,
    })

    useEffect(() => {
        setStartTime(Date.now())
    }, [stage])

    useEffect(() => {
        if (stage === Stage.Done || stage === Stage.Loading || stage === Stage.Waiting) {
            return
        }

        const interval = setInterval(() => {
            const secondsPast = Math.floor((Date.now() - startTime) / 1000)

            setTimers((prev) => ({
                ...prev,
                [stage]: secondsPast,
            }))
        }, 10)

        return () => {
            clearInterval(interval)
        }
    }, [stage, startTime])

    const getTime = (stage: 0 | 1 | 2 | 3 | 4) => {
        if (stage === Stage.Done || stage === Stage.Loading || stage === Stage.Waiting) {
            return 0
        } else {
            const secondsPast = timers[stage]
            return secondsPast
        }
    }

    return (
        <nav aria-label="Progress">
            <ol className="space-y-4 md:flex md:space-y-0 md:space-x-8">
                {steps.map((step) => (
                    <li key={step.name} className="md:flex-1">
                        {step.stage < stage ? (
                            <div className="group border-primary-800 flex flex-col border-l-4 py-2 pl-4 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0">
                                <span className="text-primary-800 text-sm font-medium">{step.name}</span>
                                <span className="text-sm font-medium">{formatTime(getTime(step.stage))}</span>
                            </div>
                        ) : step.stage === stage ? (
                            <div
                                aria-current="step"
                                className="border-primary-600 flex flex-col border-l-4 py-2 pl-4 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0"
                            >
                                <span className="text-primary-600 text-sm font-medium">{step.name}</span>
                                <span className="text-sm font-medium">{formatTime(getTime(step.stage))}</span>
                            </div>
                        ) : (
                            <div className="group flex flex-col border-l-4 border-gray-200 py-2 pl-4 hover:border-gray-300 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0">
                                <span className="text-sm font-medium text-gray-500 group-hover:text-gray-700">
                                    {step.name}
                                </span>
                                <span className="text-sm font-medium">{formatTime(getTime(step.stage))}</span>
                            </div>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    )
}
